import { canonicalize, parseCsvLine, splitCsvLines } from '../../utils/csv';

// CP-857: SiteList maintenance helpers — column definitions, CSV parsing for the bulk
// import, and the client-side mirror of the server's validation rules.

// The SiteList columns, in the order the table is read and written.
// `key` is the API/grid field name (the procs alias the real column names, which contain
// spaces); `label` is what the user sees; `hint` is the full name behind an abbreviation,
// shown on hover in the grid header and the form.
//
// SL (Service Lead) sits with the other two attribution roles it is reported alongside —
// DBD, AM, SL. It is the newest column: without it the weekly service reporting is
// assembled by hand.
export const SITE_LIST_FIELDS = [
  { key: 'BranchName', label: 'Branch Name', type: 'text', required: true },
  { key: 'BranchNumber', label: 'Branch Number', type: 'number', required: true },
  { key: 'CostCenter', label: 'Cost Center', type: 'number' },
  { key: 'DBD', label: 'DBD', hint: 'Director Business Development', type: 'text' },
  { key: 'AM', label: 'AM', hint: 'Account Manager', type: 'text' },
  { key: 'SL', label: 'SL', hint: 'Service Lead', type: 'text' },
  { key: 'Area', label: 'Area', type: 'text' },
  { key: 'LOB', label: 'LOB', hint: 'Line of Business', type: 'select', required: true },
  { key: 'RRM', label: 'RRM', hint: 'Regional Recruiting Manager', type: 'text' },
  { key: 'RM', label: 'RM', hint: 'Recruiting Manager', type: 'text' },
  { key: 'Market_Wage', label: 'Market Wage', type: 'number' },
];

// PRST does not split skilled trades by line of business, so every PRST row carries
// LOB = 'All'; Centerline's rollup rows use the real codes. Always offered, whatever the
// EDGE code table returns.
export const ALL_LOB_OPTION = { label: 'All', value: 'All' };

// Branch Number 1 is the reserved rollup branch (AllDMS / AllDMSPT / AllFlex / AllMDS).
// It has no operational branch behind it, so the server skips the branch-existence check
// for it and the grid marks it.
export const ROLLUP_BRANCH_NUMBER = 1;

export const emptyRow = () => SITE_LIST_FIELDS.reduce((row, field) => ({ ...row, [field.key]: '' }), {});

// The people/attribution fields. These are free text today, which is how typos get in and
// break attribution in the Power BI joins. Until they become a real pick-list or a people-
// directory lookup, the screen warns when a value is not one already in use — the minimum
// bar CP-857 asks for (TRIM + "new value" warning).
export const PEOPLE_FIELDS = ['DBD', 'AM', 'SL', 'RRM', 'RM', 'Area'];

/**
 * Distinct existing values per people field, for the "new value" warning.
 * Built from the rows already loaded in the grid, so it costs no extra request.
 */
export const knownValuesByField = (rows = []) =>
  PEOPLE_FIELDS.reduce((out, key) => {
    const values = new Set();
    rows.forEach(row => {
      const value = String(row?.[key] ?? '').trim();
      if (value) values.add(value);
    });
    return { ...out, [key]: values };
  }, {});

/**
 * Which people fields hold a value nobody else uses yet.
 *
 * Not an error — a genuinely new recruiter has to be typed in once. It is a nudge to catch
 * "DMS Team " vs "DMS Team" and "Jon Trahan" vs "John Trahan" before they land.
 */
export const newPeopleValues = (row, knownValues = {}) =>
  PEOPLE_FIELDS.filter(key => {
    const value = String(row?.[key] ?? '').trim();
    return value && knownValues[key] && !knownValues[key].has(value);
  });

/**
 * Map the /line-of-business response to select options.
 *
 * The EDGE code table returns { LineOfBusinessCode, LineOfBusinessDesc }. SiteList.LOB was
 * hand-maintained and stores whichever form the spreadsheet used, and the server accepts
 * either, so the option value is the code and the label shows both when they differ.
 */
export const toLobOptions = (list = []) => {
  const options = list
    .map(item => {
      const code = String(item?.LineOfBusinessCode ?? '').trim();
      const desc = String(item?.LineOfBusinessDesc ?? '').trim();
      if (!code && !desc) return null;
      const value = code || desc;
      return { value, label: desc && desc !== code ? `${value} - ${desc}` : value };
    })
    .filter(Boolean);

  // 'All' first, and never duplicated if the code table happens to contain it too.
  return [ALL_LOB_OPTION, ...options.filter(option => option.value !== ALL_LOB_OPTION.value)];
};

/**
 * Client-side validation for the add/edit form.
 *
 * Mirrors the stored procedure's rules so the user gets immediate feedback, but it is not
 * the enforcement point — sp_cl_cp_bi_sitelist_upsert re-checks everything, including the
 * duplicate-key and branch-existence rules that need the database.
 *
 * Returns an { fieldKey: message } map; empty means valid.
 */
export const validateRow = (row, { lobOptions = [] } = {}) => {
  const errors = {};

  if (!String(row.BranchName ?? '').trim()) {
    errors.BranchName = 'Branch Name is required.';
  }

  const branchNumber = String(row.BranchNumber ?? '').trim();
  if (!branchNumber) {
    errors.BranchNumber = 'Branch Number is required.';
  } else if (!/^\d+$/.test(branchNumber)) {
    errors.BranchNumber = 'Branch Number must be a whole number.';
  }

  const costCenter = String(row.CostCenter ?? '').trim();
  if (costCenter && !/^\d+$/.test(costCenter)) {
    errors.CostCenter = 'Cost Center must be a whole number.';
  }

  const marketWage = String(row.Market_Wage ?? '').trim();
  if (marketWage && (Number.isNaN(Number(marketWage)) || Number(marketWage) < 0)) {
    errors.Market_Wage = 'Market Wage must be a positive number.';
  }

  const lob = String(row.LOB ?? '').trim();
  if (!lob) {
    errors.LOB = 'LOB is required.';
  } else if (lobOptions.length && !lobOptions.some(option => option.value === lob)) {
    errors.LOB = 'Choose an LOB from the list.';
  }

  return errors;
};

/**
 * Trim every text field. The server TRIMs too — this is the belt to its braces — but doing
 * it here means what the user sees saved is what they see in the grid.
 *
 * Blank numerics are sent as '' and the API maps them to NULL: Cost Center and Market Wage
 * are genuinely blank on the rollup rows.
 */
export const normalizeRow = row =>
  SITE_LIST_FIELDS.reduce((out, field) => ({ ...out, [field.key]: String(row[field.key] ?? '').trim() }), {});

/**
 * Turn one audit row into a list of { label, from, to } for display.
 *
 * The audit table names its columns `Old<key>` / `New<key>` for exactly the keys in
 * SITE_LIST_FIELDS, so the before/after pair for every field is already on the row — the
 * screen just has to pair them up.
 *
 *   UPDATE      -> only the fields whose value actually moved
 *   INSERT/SEED -> the values the row was created with (from is null)
 *   DELETE      -> the values the row held when it was removed (to is null)
 *
 * Blank and null are treated as the same "no value", so a '' -> null rewrite is not
 * reported as a change.
 */
export const auditFieldChanges = (entry = {}) => {
  const action = String(entry.ChangeAction ?? '').toUpperCase();
  const norm = value => (value === null || value === undefined ? '' : String(value).trim());

  return SITE_LIST_FIELDS.map(field => {
    const from = norm(entry[`Old${field.key}`]);
    const to = norm(entry[`New${field.key}`]);

    if (action === 'DELETE') {
      return from ? { label: field.label, from, to: null } : null;
    }
    if (action === 'INSERT' || action === 'SEED') {
      return to ? { label: field.label, from: null, to } : null;
    }
    // UPDATE — report the fields that moved
    return from === to ? null : { label: field.label, from, to };
  }).filter(Boolean);
};

/**
 * Render an audit timestamp for display. ChangedAt is stored UTC, and the driver hands it
 * back as UTC, so this is the one place that turns it into the reader's local time.
 */
export const formatWhen = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const toIsoWhen = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
};

/**
 * Change history shaped for ExportCsvButton.
 *
 * LONG format — one row per changed field, not one per audit entry. The question this
 * export exists to answer is "who was the SL on this branch in July, and when did it
 * change", which in long format is a filter on two columns (Field = SL, Branch Number) in
 * Excel or a single pass for anything reading the file. A wide row of 22 before/after
 * columns makes the same question a manual diff.
 *
 * An entry with no field changes still emits one row, so the export never silently holds
 * fewer entries than the screen shows.
 *
 * Two timestamps by design: `When` matches the screen exactly (local, so the two reconcile)
 * and `When (ISO)` is sortable — a locale string sorts as text, which puts 10/1 before 9/1.
 */
export const toAuditCsvData = (entries = []) =>
  entries.flatMap(entry => {
    const changes = auditFieldChanges(entry);
    const rows = changes.length ? changes : [{ label: '', from: '', to: '' }];

    return rows.map(({ label, from, to }) => ({
      When: formatWhen(entry.ChangedAt),
      'When (ISO)': toIsoWhen(entry.ChangedAt),
      Who: entry.ChangedBy ?? '',
      Action: entry.ChangeAction ?? '',
      'Branch Number': entry.BranchNumber ?? '',
      LOB: entry.LOB ?? '',
      'Branch Name': entry.BranchName ?? '',
      Field: label,
      From: from ?? '',
      To: to ?? '',
      'Audit Id': entry.AuditId ?? '',
      // Groups the rows of one bulk import, which is how a whole previous list is recovered.
      'Batch Id': entry.BatchId ?? '',
    }));
  });

/**
 * Parse a pasted or uploaded CSV into SiteList rows.
 *
 * Hand-rolled rather than pulling in a CSV library, matching the repo's existing approach
 * (react-csv is export-only). Handles quoted fields, escaped quotes ("") and commas inside
 * quotes — the Branch Name column is "Commerce, CA", so comma-splitting is not an option.
 *
 * The header row maps by label OR key, case- and space-insensitively, so a file exported
 * from this screen and the spreadsheet the team already maintains both work.
 *
 * Returns { rows, errors } — errors are structural (unknown/missing columns), not value
 * validation, which the server does.
 */
export const parseCsv = text => {
  const lines = splitCsvLines(String(text ?? '').trim());
  if (lines.length < 2) {
    return { rows: [], errors: ['The file needs a header row and at least one data row.'] };
  }

  // Keep the headers as written as well as canonicalized: matching has to ignore case and
  // spacing, but a message about an unrecognized column must echo what the user typed.
  const rawHeaderCells = parseCsvLine(lines[0]).map(cell => String(cell ?? '').trim());
  const headerCells = rawHeaderCells.map(cell => canonicalize(cell));
  const fieldByHeader = new Map();
  SITE_LIST_FIELDS.forEach(field => {
    fieldByHeader.set(canonicalize(field.label), field.key);
    fieldByHeader.set(canonicalize(field.key), field.key);
  });

  const columnKeys = headerCells.map(header => fieldByHeader.get(header) ?? null);

  const missing = SITE_LIST_FIELDS.filter(field => field.required && !columnKeys.includes(field.key)).map(
    field => field.label,
  );
  if (missing.length) {
    return { rows: [], errors: [`Missing required column(s): ${missing.join(', ')}.`] };
  }

  const unknown = rawHeaderCells.filter((header, index) => columnKeys[index] === null && header);
  const errors = unknown.length ? [`Ignoring unrecognized column(s): ${unknown.join(', ')}.`] : [];

  // A column absent from the file is imported as blank, and a Merge writes that blank over
  // whatever the row holds today — so an older spreadsheet that predates a column silently
  // clears it on every branch it touches. Warn rather than block: importing a subset of
  // columns to deliberately clear them is legitimate, it just has to be on purpose.
  const absent = SITE_LIST_FIELDS.filter(field => !field.required && !columnKeys.includes(field.key)).map(
    field => field.label,
  );
  if (absent.length) {
    errors.push(
      `Not in the file: ${absent.join(', ')}. Those columns will be saved as blank — ` +
        'add them to the file to keep the current values.',
    );
  }

  const rows = lines.slice(1).map(line => {
    const cells = parseCsvLine(line);
    const row = emptyRow();
    columnKeys.forEach((key, index) => {
      if (key) {
        row[key] = String(cells[index] ?? '').trim();
      }
    });
    return row;
  });

  // A trailing newline yields one blank row; drop anything with nothing in it.
  return { rows: rows.filter(row => Object.values(row).some(value => value !== '')), errors };
};

/**
 * Rows shaped for ExportCsvButton — labels as headers, so an exported file can be edited
 * and fed straight back through the import.
 */
export const toCsvData = (rows = []) =>
  rows.map(row => SITE_LIST_FIELDS.reduce((out, field) => ({ ...out, [field.label]: row[field.key] ?? '' }), {}));
